import { memo, useCallback } from 'react';

import { useSelector } from 'react-redux';
import { CDN_BASE_URL } from 'settings';
import { Divider, If } from 'shared';
import ErrorMessage from 'shared/ErrorMessage';
import { Column } from 'shared/Houston';
import { selectMediaQuery } from 'store/selectors';
import formatCurrency from 'utils/formatCurrency';

import { IOffer, IProduct } from '../../types/interfaces/product';
import OfferTag from './OfferTag';
import {
  ContentProduct,
  Image,
  ProductId,
  Price,
  Item,
  ProductTitle,
  ProductDetail,
  ProductDetailLine,
  ContentRow
} from './styles';

interface IProps {
  product?: IProduct;
  action?: React.ReactNode;
  className?: string;
  offer?: IOffer;
  fontRegular?: boolean;
  hideErrorMessage?: boolean;
  hideId?: boolean;
  isPreview?: boolean;
  footer?: React.ReactNode;
}

const SelectedProductItem: React.FC<IProps> = ({
  product,
  action,
  className,
  offer,
  fontRegular = false,
  hideErrorMessage,
  hideId,
  isPreview = false,
  footer
}) => {
  const media = useSelector(selectMediaQuery);

  const getOffer = useCallback(
    (offerId: number | undefined) => {
      if (!offerId) {
        return null;
      }

      return product?.offers?.find(offer => String(offer.offerId) === String(offerId));
    },
    [product]
  );

  const getOfferPrice = useCallback(
    (offerId: number | undefined) => {
      const offer = getOffer(offerId);

      if (!offer || Number(offer?.offerValue) === 0) {
        return null;
      }

      return offer.offerValue;
    },
    [getOffer]
  );

  if (!product && !offer) {
    return null;
  }

  const hasChildren = product?.children && product?.children.length > 0;
  const hasOffer = product?.offers && product?.offers?.length > 0;

  return (
    <Item className={className} alignItems='center' justifyContent='space-between'>
      <If condition={!!product}>
        <ContentRow>
          <ContentProduct>
            <Image
              src={product?.image}
              alt={`Imagem do produto ${product?.title}`}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = `${CDN_BASE_URL}/default-img.svg`;
              }}
            />
            <ProductDetail>
              <ProductDetailLine>
                <ProductId size='xxs'>
                  <If condition={!hideId && !isPreview}>
                    #{!hasChildren || !hasOffer ? product?.contentId : product?.offerKey}
                  </If>
                  <If condition={!hideId && isPreview}>#{product?.offerKey ?? product?.contentId}</If>
                  <OfferTag hasOffer={product?.hasOffer || false} />
                </ProductId>
                <If condition={!hasChildren && !hideErrorMessage && product?.errorMsg}>
                  <ErrorMessage message={product?.errorMsg || ''} />
                </If>
              </ProductDetailLine>
              {fontRegular ? (
                <ProductTitle size='xs'>{product?.title}</ProductTitle>
              ) : (
                <ProductTitle size='xs' weight='semibold'>
                  {product?.title}
                </ProductTitle>
              )}
              <If condition={!!product?.offerName || product?.offerId}>
                <ProductTitle size='xs'>{product?.offerName || getOffer(product?.offerId)?.offerName}</ProductTitle>
              </If>
              <Price size='xs'>{formatCurrency(Number(getOfferPrice(product?.offerId) ?? product?.price))}</Price>
            </ProductDetail>
            {footer}
          </ContentProduct>
          <If condition={!!action}>
            <If condition={!!media.md}>
              <Divider margin='16px 0' />
            </If>
            <Column>{action}</Column>
          </If>
        </ContentRow>
      </If>

      <If condition={!!offer}>
        <ContentProduct>
          <ProductDetail>
            <ProductId>#{offer?.offerKey}</ProductId>
            <ProductTitle size='xs'>
              {offer?.offerName} &nbsp;
              <If condition={!hideErrorMessage && offer?.errorMsg}>
                <ErrorMessage message={offer?.errorMsg || ''} />
              </If>
            </ProductTitle>
            <Price size='xs'>{formatCurrency(Number(offer?.offerValue))}</Price>
          </ProductDetail>
          <If condition={!!action}>
            <If condition={!!media.md}>
              <Divider margin='16px 0' />
            </If>
            <Column>{action}</Column>
          </If>
        </ContentProduct>
      </If>
    </Item>
  );
};

export default memo(SelectedProductItem);
